import React, { useState, useEffect } from 'react';
import CustomDropdown from '../common/CustomDropdown';
import { searchService, tenderService } from '../../services/api';
import { PLAYER_POSITIONS, COACH_POSITIONS, STAFF_POSITIONS } from '../../constants/tenderChoices';

const FALLBACK_CONTINENTS = [
  { value: 'Africa', label: 'Africa' },
  { value: 'Asia', label: 'Asia' },
  { value: 'Europe', label: 'Europe' },
  { value: 'North America', label: 'North America' },
  { value: 'Oceania', label: 'Oceania' },
  { value: 'South America', label: 'South America' },
];

const TenderFilterModal = ({
  isOpen,
  onClose,
  onApply,
  initialFilters,
  selectedType = "player",
  // DECISION 050 follow-up: which tab of the request list this modal is
  // open on, and whether the TP-Lite sub-tab is active. The counts follow
  // both, because the list does. Left out -> the backend keeps its
  // original active-marketplace behaviour.
  activeTab = "",
  isLite = undefined,
}) => {
  const [filters, setFilters] = useState(initialFilters);
  // Full country catalogue — used by the Nationality dropdown (player
  // nationality is independent of where tenders are posted) and as a
  // lookup for flag image URLs on the Country dropdown.
  const [countries, setCountries] = useState([]);
  // Consolidated tender-filter options from /dashboard/tenders/filters/.
  // Each list is pre-filtered to values that actually appear on active
  // tenders, with a tender_count per option (used for "Spain (12)" labels).
  // Cascades on country + continent — picking one narrows the rest.
  const [tenderFilters, setTenderFilters] = useState({
    countries: [],
    divisions: [],
    positions: [],
    continents: [],
  });
  // eslint-disable-next-line no-unused-vars
  const [filtersLoading, setFiltersLoading] = useState(false);

  // Update filters when initialFilters change (when modal opens)
  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters, isOpen]);

  // Full countries list — Nationality dropdown + flag-image lookup map.
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await searchService.getCountries();
        if (response.success && response.data) {
          setCountries(response.data);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    if (isOpen) fetchCountries();
  }, [isOpen]);

  // Consolidated tender-filter options (countries / divisions / positions /
  // continents) with per-option tender counts. Re-fires when the user
  // changes country or continent so dependent dropdowns narrow (cascade).
  useEffect(() => {
    if (!isOpen) return undefined;
    const controller = new AbortController();
    const categoryParam =
      selectedType === "players" || selectedType === "player"
        ? "players"
        : selectedType === "coaches" || selectedType === "coach"
        ? "coaches"
        : selectedType === "staffs" || selectedType === "staff"
        ? "staffs"
        : selectedType === "others"
        ? "others"
        : "";
    setFiltersLoading(true);
    (async () => {
      try {
        const response = await tenderService.getTenderFilters({
          country: filters.country || "",
          continent: filters.continent || "",
          tender_category: categoryParam,
          // DECISION 050: this modal belongs to the CLASSIC club-request list,
          // which never shows agent-marketplace rows — so its counts must not
          // include them either. Without this the option said "(1)" and the
          // list came back empty.
          surface: "classic",
          tab: activeTab,
          is_lite: isLite,
          signal: controller.signal,
        });
        if (response.success && response.data) {
          setTenderFilters(response.data);
        }
      } catch (error) {
        if (error?.name !== "CanceledError") {
          console.error("Error fetching tender filters:", error);
        }
      } finally {
        setFiltersLoading(false);
      }
    })();
    return () => controller.abort();
  }, [isOpen, filters.country, filters.continent, selectedType, activeTab, isLite]);

  // Use tender-driven continent list when present; fall back to the static
  // six-continent list so the dropdown stays usable on empty datasets.
  const continentOptions = tenderFilters.continents.length > 0
    ? tenderFilters.continents.map((c) => ({
        value: c.name,
        label: `${c.name} (${c.tender_count})`,
      }))
    : FALLBACK_CONTINENTS;

  // Get positions based on selected type
  const getPositions = () => {
    // Handle all 4 types: players, coaches, staffs, others
    if (selectedType === "players" || selectedType === "player") {
      return PLAYER_POSITIONS;
    } else if (selectedType === "coaches" || selectedType === "coach") {
      return COACH_POSITIONS;
    } else if (selectedType === "staffs" || selectedType === "staff") {
      return STAFF_POSITIONS;
    }
    // For "others" - use STAFF_POSITIONS as default
    return STAFF_POSITIONS;
  };

  const positions = getPositions();

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'deal', label: 'Deal' },
    { value: 'expired', label: 'Expired' }
  ];

  // Tender type options based on selected type
  // Players: Buy, Loan, Trial
  // Coaches/Staffs/Others: Full Time, Contract, Temporary
  const tenderTypeOptions = (selectedType === "players" || selectedType === "player")
    ? [
        { value: 'all', label: 'All Request Types' },
        { value: 'buy', label: 'Buy' },
        { value: 'loan', label: 'Loan' },
        { value: 'trial', label: 'Trial' }
      ]
    : [
        { value: 'all', label: 'All Request Types' },
        { value: 'full_time', label: 'Full Time' },
        { value: 'contract', label: 'Contract' },
        { value: 'temporary', label: 'Temporary' }
      ];

  const sortOptions = [
    { value: 'created_desc', label: 'Newest First' },
    { value: 'created_asc', label: 'Oldest First' },
    { value: 'market_value_desc', label: 'Highest Market Value' },
    { value: 'market_value_asc', label: 'Lowest Market Value' },
  ];

  const handleChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleClearAll = () => {
    const defaultFilters = {
      market_value_from: "",
      market_value_to: "",
      division: "",
      position: "",
      nationality: "",
      min_age: "",
      max_age: "",
      status: "all",
      search: "",
      only_eu_player: false,
      only_free_players: false,
      sort_by: "",
      country: "",
      continent: "",
      type_of_tender: "all",
    };
    setFilters(defaultFilters);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 transition-opacity duration-300 ease-out">
      <div className="
      bg-white rounded-t-2xl sm:rounded-lg
      p-6 w-full sm:max-w-2xl
      max-h-[90vh] overflow-y-auto
      transform transition-all duration-300 ease-out
      translate-y-0 sm:translate-y-0
      motion-safe:animate-[slideUp_0.3s_ease-out_forwards]
    ">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Filter by</h2>
          <button onClick={onClose} className="text-secondary hover:text-gray-900">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Market Value Range */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Market Value</h3>
            {(filters.market_value_from || filters.market_value_to) && (
              <button
                onClick={() => {
                  handleChange('market_value_from', '');
                  handleChange('market_value_to', '');
                }}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm text-gray-600 mb-1">From</label>
              <input
                type="number"
                value={filters.market_value_from}
                onChange={(e) => handleChange('market_value_from', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 placeholder:text-[#898D95]"
                placeholder="Min value"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm text-gray-600 mb-1">To</label>
              <input
                type="number"
                value={filters.market_value_to}
                onChange={(e) => handleChange('market_value_to', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 placeholder:text-[#898D95]"
                placeholder="Max value"
              />
            </div>
          </div>
        </div>

        {/* Continent */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Continent</h3>
            {filters.continent && (
              <button
                onClick={() => handleChange('continent', '')}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <CustomDropdown
            options={continentOptions}
            value={filters.continent}
            onChange={(value) => handleChange('continent', value)}
            placeholder="All Continents"
            displayProperty="label"
            valueProperty="value"
            searchEnabled={true}
            searchPlaceholder="Search continent..."
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Country */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Country</h3>
            {filters.country && (
              <button
                onClick={() => handleChange('country', '')}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <CustomDropdown
            options={tenderFilters.countries.map((c) => {
              // Look up flag image URL from the full countries list as a
              // fallback for environments where flag-icons CSS isn't loaded.
              const match = countries.find(
                (x) => (x.name || "").toLowerCase() === (c.name || "").toLowerCase(),
              );
              return {
                value: c.name,
                label: `${c.name} (${c.tender_count})`,
                iconClassName: c.code ? `fi fi-${c.code}` : undefined,
                image: match?.image_url,
              };
            })}
            value={filters.country}
            onChange={(value) => handleChange('country', value)}
            placeholder="All Countries"
            displayProperty="label"
            valueProperty="value"
            searchEnabled={true}
            searchPlaceholder="Search country..."
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Division */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Division</h3>
            {filters.division && (
              <button
                onClick={() => handleChange('division', '')}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <CustomDropdown
            options={tenderFilters.divisions.map((d) => ({
              value: d.name,
              label: d.country
                ? `${d.name} — ${d.country} (${d.tender_count})`
                : `${d.name} (${d.tender_count})`,
            }))}
            value={filters.division}
            onChange={(value) => handleChange('division', value)}
            placeholder="All Divisions"
            displayProperty="label"
            valueProperty="value"
            searchEnabled={true}
            searchPlaceholder="Search division..."
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Position */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Position</h3>
            {filters.position && (
              <button
                onClick={() => handleChange('position', '')}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <CustomDropdown
            options={(() => {
              // Merge the static position catalog (for selectedType) with the
              // backend's per-position counts. Positions with zero active
              // tenders still appear (so the user can pre-set a filter that
              // becomes useful as new tenders arrive) but sort to the bottom.
              const countsByValue = Object.fromEntries(
                tenderFilters.positions.map((p) => [p.value, p.tender_count]),
              );
              return positions
                .map((p) => ({
                  value: p.value,
                  label: countsByValue[p.value]
                    ? `${p.label} (${countsByValue[p.value]})`
                    : p.label,
                  _count: countsByValue[p.value] || 0,
                }))
                .sort((a, b) => b._count - a._count);
            })()}
            value={filters.position}
            onChange={(value) => handleChange('position', value)}
            placeholder="All Positions"
            displayProperty="label"
            valueProperty="value"
            searchEnabled={true}
            searchPlaceholder="Search position..."
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 "
          />
        </div>

        {/* Nationality */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Nationality</h3>
            {filters.nationality && (
              <button
                onClick={() => handleChange('nationality', '')}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <CustomDropdown
            options={countries.map(country => ({
              value: country.name,
              label: country.name,
              iconClassName: country.country_code ? `fi fi-${country.country_code}` : undefined,
              image: country.image_url
            }))}
            value={filters.nationality}
            onChange={(value) => handleChange('nationality', value)}
            placeholder="All Nationalities"
            displayProperty="label"
            valueProperty="value"
            searchEnabled={true}
            searchPlaceholder="Search nationality..."
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Age Range */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Age Range</h3>
            {(filters.min_age || filters.max_age) && (
              <button
                onClick={() => {
                  handleChange('min_age', '');
                  handleChange('max_age', '');
                }}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm text-gray-600 mb-1">Min Age</label>
                             <input
                 type="number"
                 value={filters.min_age || ''}
                 onChange={(e) => handleChange('min_age', e.target.value)}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 placeholder:text-[#898D95]"
                 placeholder="Min age"
                 min="12"
                 max="50"
               />
            </div>
            <div className="flex-1">
              <label className="block text-sm text-gray-600 mb-1">Max Age</label>
                             <input
                 type="number"
                 value={filters.max_age || ''}
                 onChange={(e) => handleChange('max_age', e.target.value)}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 placeholder:text-[#898D95]"
                 placeholder="Max age"
                 min="12"
                 max="50"
               />
            </div>
          </div>
        </div>



        {/* Tender Type */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Request Type</h3>
            {filters.type_of_tender && filters.type_of_tender !== 'all' && (
              <button
                onClick={() => handleChange('type_of_tender', 'all')}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <CustomDropdown
            options={tenderTypeOptions}
            value={filters.type_of_tender || 'all'}
            onChange={(value) => handleChange('type_of_tender', value)}
            placeholder="All Request Types"
            displayProperty="label"
            valueProperty="value"
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Status */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Status</h3>
            {filters.status && filters.status !== 'all' && (
              <button
                onClick={() => handleChange('status', 'all')}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <CustomDropdown
            options={statusOptions}
            value={filters.status || 'all'}
            onChange={(value) => handleChange('status', value)}
            placeholder="All Status"
            displayProperty="label"
            valueProperty="value"
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Player-specific filters */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Player Options</h3>
            {(filters.only_eu_player || filters.only_free_players) && (
              <button
                onClick={() => {
                  handleChange('only_eu_player', false);
                  handleChange('only_free_players', false);
                }}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <div className="space-y-3">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={filters.only_eu_player || false}
                onChange={(e) => handleChange('only_eu_player', e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Only EU Players</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={filters.only_free_players || false}
                onChange={(e) => handleChange('only_free_players', e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Only Free Players</span>
            </label>
          </div>
        </div>

        {/* Sort By */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Sort By</h3>
            {filters.sort_by && (
              <button
                onClick={() => handleChange('sort_by', '')}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <CustomDropdown
            options={sortOptions}
            value={filters.sort_by || ''}
            onChange={(value) => handleChange('sort_by', value)}
            placeholder="Default Sorting"
            displayProperty="label"
            valueProperty="value"
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>



        {/* Action Buttons */}
        <div className="flex gap-4 flex-col sm:flex-row">
          <button
            onClick={handleClearAll}
            className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
          >
            Clear All
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenderFilterModal;